import React, { useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import FileUploadInput from "components/FileUploadInput";
import { isBlank, notBlank } from "../../util/ext";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, DialogContentText, DialogTitle } from "@mui/material";
import { useErrorBox } from "../../manage-organizations/edit/use-errorBox";
import PhotoOutlinedIcon from "@mui/icons-material/PhotoOutlined";
import { useOrganizationLogoUpload } from "../cspServices";
import OrganizationAutoComplete from "../../user-admin/OrganizationAutoComplete";
import Grid from "@mui/material/Grid";

const OrganizationLogoDialog = ({ projectId, onCancel, onSave, open }) => {
  const [organizationId, setOrganizationId] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const onFileInputChange = file => {
    setLogoFile(file);
    if (notBlank(file)) {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        setLogoUrl(dataUrl);
      };
      reader.readAsDataURL(file);
      removeValidationError("logoFile");
    } else {
      setLogoUrl(null);
    }
  };

  const {
    setGeneralError,
    addValidationError,
    hasValidationError,
    firstValidationError,
    removeValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const logoUpload = useOrganizationLogoUpload();
  const handleApply = async () => {
    if (isBlank(organizationId)) {
      addValidationError({
        organizationId: ["An Organization must be selected"],
      });
    } else {
      if (notBlank(logoFile)) {
        setUploading(true);
        try {
          const orgLogoLocation = await logoUpload.mutateAsync({
            resourceIds: { organizationId, projectId },
            data: logoFile,
          });
          onSave(orgLogoLocation);
        } catch (err) {
          console.log(err.body);
          setGeneralError(err);
        } finally {
          setUploading(false);
        }
      } else {
        addValidationError({ logoFile: ["An image must be selected"] });
      }
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle id="edit-organization-logo-dialog-title">
        Organization Logo
      </DialogTitle>

      <DialogContent>
        <DialogContentText>
          Choose an Organization and the image you wish to upload.
        </DialogContentText>
        <DialogErrors />
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12}>
            <OrganizationAutoComplete
              value={organizationId}
              filterFunction={org => org.active}
              onChange={setOrganizationId}
              error={hasValidationError("organizationId")}
              helperText={firstValidationError("organizationId")}
            />
          </Grid>
        </Grid>

        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          maxWidth={600}
        >
          <Box
            margin={2}
            minWidth={250}
            minHeight={150}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {uploading ? (
              <CircularProgress />
            ) : isBlank(logoUrl) ? (
              <PhotoOutlinedIcon style={{ fontSize: 60 }} />
            ) : (
              <img
                style={{ maxWidth: 250, maxHeight: 150 }}
                src={logoUrl}
                alt="Logo"
              />
            )}
          </Box>

          <Box flexGrow={1} margin={2} width={250}>
            <FileUploadInput
              value={logoFile}
              accept=".png, image/png"
              onChange={onFileInputChange}
              disabled={uploading}
              error={firstValidationError("logoFile")}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button disabled={uploading} onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button disabled={uploading} onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationLogoDialog;
